import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

export type BarPreset = 'dark' | 'light';

const STATUS_PRESETS: Record<BarPreset, { color: string; style: Style }> = {
  // Blue background, white icons (over #3361b7 / blue gradient / newbg.png)
  dark: { color: '#3361b7', style: Style.Dark },
  // Light background, dark icons (over #ffffff / #f1f1f1)
  light: { color: '#ffffff', style: Style.Light },
};

const NAV_PRESETS: Record<BarPreset, string> = {
  dark: '#6f9bdc',
  light: '#ffffff',
};

const DEFAULT_TOP: BarPreset = 'dark';
const DEFAULT_NAV: BarPreset = 'light';

@Injectable({ providedIn: 'root' })
export class StatusBarService {
  private router = inject(Router);
  private lastTop: BarPreset | null = null;
  private lastNav: BarPreset | null = null;

  initialize(): void {
    if (Capacitor.getPlatform() !== 'android') return;

    this.applyFromSnapshot();

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.applyFromSnapshot());
  }

  private applyFromSnapshot(): void {
    const { top, nav } = this.readPresetsFromSnapshot(this.router.routerState.snapshot.root);
    this.applyTop(top);
    this.applyNav(nav);
  }

  private readPresetsFromSnapshot(root: ActivatedRouteSnapshot): { top: BarPreset; nav: BarPreset } {
    let node: ActivatedRouteSnapshot = root;
    let top: BarPreset = DEFAULT_TOP;
    let nav: BarPreset = DEFAULT_NAV;
    while (node) {
      const topValue = node.data?.['topBar'];
      if (topValue === 'dark' || topValue === 'light') top = topValue;
      const navValue = node.data?.['navBar'];
      if (navValue === 'dark' || navValue === 'light') nav = navValue;
      if (!node.firstChild) break;
      node = node.firstChild;
    }
    return { top, nav };
  }

  private async applyTop(preset: BarPreset): Promise<void> {
    if (preset === this.lastTop) return;
    this.lastTop = preset;
    const { color, style } = STATUS_PRESETS[preset];
    try {
      await EdgeToEdge.setStatusBarColor({ color });
    } catch { /* no-op on web/iOS */ }
    try {
      await StatusBar.setStyle({ style });
    } catch { /* no-op on web */ }
  }

  private async applyNav(preset: BarPreset): Promise<void> {
    if (preset === this.lastNav) return;
    this.lastNav = preset;
    try {
      await EdgeToEdge.setNavigationBarColor({ color: NAV_PRESETS[preset] });
    } catch { /* no-op on web/iOS */ }
  }
}
